import { GeocodeService } from "@app/services/geocode.service";
import { VideoCoachingStatus } from "@components/videos/enums/VideoCoachingStatus.enum";
import { VideoReviewStatus } from "@components/videos/enums/VideoReviewStatus.enum";
import { VideoReviewSubStatus } from "@components/videos/enums/VideoReviewSubStatus.enum";
import { IVeriMapComponent } from "@directives/_map/veriMap/veriMapComponent/veriMap.component";
import { IVeriTag } from "@directives/_veri/v-tags/v-tags.component";
import { IGoogleMapsMarker } from "@interfaces/IGoogleMapsMarkerOptions";
import { IJourney } from "@interfaces/IJourney";
import { IPlot } from "@interfaces/IPlot";
import { IUser } from "@interfaces/IUser";
import { ChartService } from "@services/charts.service";
import { CurrentUserService } from "@services/currentUser.service";
import { DriverAssignmentService } from "@services/driverAssignment.service";
import { GoogleMarkerService, ICreateEventMarkerOptions } from "@services/googleMarker.service";
import { JourneyService } from "@services/journey.service";
import { ObservationsService } from "@services/observations.service";
import { TranslationService } from "@services/translation.service";
import { UsersService } from "@services/users.service";
import { IPolyline } from "@typings/google.maps.angular";
import { CameraDisplayService } from "@videos/cameraDisplay.service";
import * as angular from "angular";
import * as moment from "moment";
import { CoordsModel } from "../../livemap/model/CoordsModel";
import { ProductVendor } from "../enums/CameraProductType";
import { VideoObservation } from "../enums/VideoObservation";
import { VideoStatus } from "../enums/VideoStatus";
import { IUserAction } from "../models/IUserAction";
import { IVideoEntry } from "../models/IVideoEntry";
import { IVideoExtensionData } from "../models/IVideoExtensionData";
import { CameraStatusService } from "../services/camera-status.service";
import { CameraService } from "../services/camera.service";
import { VideoEntryService } from "../services/video-entry.service";
import { VideoReviewStatusService } from "../services/video-review-status.service";
import { VideoReviewService } from "../services/video-review.service";
import { VideoEntryUtils } from "../utils/video-entry-utils";
import "./compositeVideoPlayer.scss";

interface IComponentBindings {
    onDelete: () => void;
    onStatusUpdate: () => void;
}

class ComponentController implements angular.IComponentController, IComponentBindings {
    // enums
    VideoObservation = VideoObservation;

    // ---

    users: IUser[];
    video: IVideoEntry;

    videoMarker: IGoogleMapsMarker<IVideoEntry>;
    startMapMarker: IGoogleMapsMarker;
    endMapMarker: IGoogleMapsMarker;

    mapCenter: CoordsModel;
    videoJourney: IJourney;
    videoJourneys: IJourney[] = [];
    showMap = false;

    showVideo: boolean;
    showDownloadProgress: boolean;
    downloadProgress: number;
    downloadStatus: string;

    videoStatusTypes = VideoStatus;
    cameraProductGroupIds = ProductVendor;

    userFavourite: boolean;
    userReviewed: boolean;

    updating: boolean;

    videoLength = 20;

    brakingAccelerationChartConfig: Highcharts.Options;
    corneringChartConfig: Highcharts.Options;

    corneringChart: Highcharts.ChartObject;
    brakingAccelerationChart: Highcharts.ChartObject;

    extensionData: IVideoExtensionData;

    videoReviewStatus: VideoReviewStatus;
    videoReviewSubStatus: VideoReviewSubStatus;
    videoCoachingStatus: VideoCoachingStatus;
    originalVideoReviewStatus: VideoReviewStatus;
    mandatoryCommentRequired: boolean = false;
    videoReviewStatusLabel: string;

    showCoachingOnStartup = false;
    selectedTab = "";
    tags: IVeriTag<VideoObservation>[] = [];
    videoObservationSearchString: string;

    videoPolyline: IPolyline;
    plotsWithSpeed: IPlot[];

    onDelete: () => void;
    onStatusUpdate: () => void;

    static $inject = ["$rootScope", "$timeout", "$q", "driverAssignmentService", "cameraDisplayService", "currentUserService", "usersService", "googleMarkerService", "geocodeService", "journeyService", "chartService", "translationService", "observationsService", "videoReviewService", "videoEntryService", "videoReviewStatusService", "cameraStatusService", "cameraService"];

    constructor(
        public readonly $rootScope: angular.IScope,
        private readonly $timeout: angular.ITimeoutService,
        private readonly $q: angular.IQService,
        private readonly driverAssignmentService: DriverAssignmentService,
        private readonly cameraDisplayService: CameraDisplayService,
        private readonly currentUserService: CurrentUserService,
        private readonly usersService: UsersService,
        private readonly googleMarkerService: GoogleMarkerService,
        private readonly geocodeService: GeocodeService,
        private readonly journeyService: JourneyService,
        private readonly chartService: ChartService,
        private readonly translationService: TranslationService,
        private readonly observationsService: ObservationsService,
        private readonly videoReviewService: VideoReviewService,
        private readonly videoEntryService: VideoEntryService,
        private readonly videoReviewStatusService: VideoReviewStatusService,
        private readonly cameraStatusService: CameraStatusService,
        private readonly cameraService: CameraService
        ) { }

    $onInit(): void {
        this.usersService.getUsers({ includeAdmins: true }).then(users => {
            this.users = users;
        });

        this.$timeout(() => {
            this.showMap = true;
        }, 300);

        this.createTags();
    }

    $onChanges() {
        if (!this.video) {
            return;
        }

        this.selectedTab = this.showCoachingOnStartup ? this.translationService.t("Videos.Coaching") : "";

        this.addMissingReviewStatus(this.video);

        this.videoEntryService.getExtensionData(this.video).then(data => {
            this.extensionData = data;

            if (this.extensionData) {
                this.createBrakingAccelerationChartOptions();
                this.createCorneringChartOptions();
            }
        });

        this.usersService.getUsers({ includeAdmins: true }).then(users => {
            this.users = users;

            this.updateVideoTimeline();
            this.videoReviewService.mapMetaDataNotesToUsers(this.video.MetaData);

            this.setFavouriteAndReviewed();
            this.populateVideoVehicleAndDriver();

            this.mapCenter = new CoordsModel(this.video.Latitude, this.video.Longitude);

            if (this.video.Status === VideoStatus.Downloaded) {
                this.videoReviewService.markVideoAsWatched(this.video).then(video => {
                    this.video.MetaData.Watched = video.MetaData.Watched;
                    this.videoReviewService.mapMetaDataNotesToUsers(this.video.MetaData);
                    this.updateVideoTimeline();
                });

                this.showVideo = true;
                this.onStatusUpdate();
            }
            else if (VideoEntryUtils.isDownloading(this.video)) {
                this.setShowDownloadProgress(true);
            }
        });

        this.createTags();
    }

    onViewReviewStatusChanged(newVideoReviewStatus: VideoReviewStatus): void {
        this.videoReviewStatus = Number(newVideoReviewStatus);

        const hasChanged = (newVideoReviewStatus != this.originalVideoReviewStatus);

        this.updateMandatoryCommentRequired(hasChanged);

        if (this.videoReviewStatus === VideoReviewStatus.Reviewed && this.videoReviewSubStatus === VideoReviewSubStatus.None) {
            this.videoReviewSubStatus = VideoReviewSubStatus.NoActionRequired;
        }

        if (this.videoReviewStatus === VideoReviewStatus.Coached && this.videoCoachingStatus === VideoCoachingStatus.None) {
            this.videoCoachingStatus = VideoCoachingStatus.DriverTrained;
        }
    }

    onViewReviewSubStatusStatusChanged(newVideoReviewSubStatus: VideoReviewSubStatus): void {
        this.videoReviewSubStatus = Number(newVideoReviewSubStatus);
    }

    onViewCoachingStatusChanged(newVideoCoachingStatus: VideoCoachingStatus): void {
        this.videoCoachingStatus = Number(newVideoCoachingStatus);
    }

    setFavouriteAndReviewed() {
        return this.currentUserService.getCurrentUser().then(user => {
            this.userReviewed = this.isUserActionSet(this.video.MetaData.Authorised, user);
            this.userFavourite = this.isUserActionSet(this.video.MetaData.Favourite, user);
        });
    }

    mapLoaded(veriMap: IVeriMapComponent) {
        this.createMapEventMarker();
    }

    onVideoRequest() {
        this.updating = true;

        const currentUserPromise = this.currentUserService.getCurrentUser();
        const usersPromise = this.usersService.getUsers({ includeAdmins: true });

        this.$q.all([currentUserPromise, usersPromise])
            .then(([user, users]) => {
                this.users = users;

                this.video.RequestedBy = user.Id;
                this.video.requestedByUser = user;
                this.video.Status = VideoStatus.UploadRequested;
                this.video.displayStatus = this.cameraStatusService.getVideoDisplayStatus(this.video);
                this.video.MetaData.UserRequestedUploadDate = new Date();

                this.updateVideoTimeline();
                this.setShowDownloadProgress(true);

            })
            .finally(() => {
                this.updating = false;
            });
    }

    addNote(noteAdded: IUserAction) {
        this.video.reviewStatus = this.videoReviewStatus;

        if (this.videoReviewStatus === VideoReviewStatus.Reviewed) {
            this.video.reviewSubStatus = this.videoReviewSubStatus;
        }
        if (this.videoReviewStatus === VideoReviewStatus.Coached) {
            this.video.coachingStatus = this.videoCoachingStatus;
        }

        this.video.reviewStatusDisplayValue = this.videoReviewStatusService.getVideoReviewStatusDisplayValue(this.video);

        this.updateModel(
            this.videoReviewService.addNoteToVideo(this.video, noteAdded).then(video => {
                this.videoReviewService.mapMetaDataNotesToUsers(this.video.MetaData);
                return video;
            }));

        const hasVideoReviewStatusChanged = (this.videoReviewStatus !== this.originalVideoReviewStatus);
        if (hasVideoReviewStatusChanged) {
            this.setVideoReviewStatus();
        }
    }

    toggleFavourite() {
        this.userFavourite = !this.userFavourite;
        this.onFavouriteChange();
        this.onStatusUpdate();
    }

    onFavouriteChange() {
        this.updateModel(this.videoReviewService.setVideoFavourite(this.video, this.userFavourite));
    }

    onReviewedChange() {
        this.updateModel(this.videoReviewService.setVideoReviewed(this.video, this.userReviewed));
    }

    updateModel(promise: Promise<IVideoEntry>): Promise<void> {
        if (this.updating) {
            return;
        }

        this.updating = true;

        return promise
            .then(() => {
                this.updateVideoTimeline();
            })
            .finally(() => {
                this.updating = false;
            });
    }

    onEditClick() {
        this.showVideo = !this.showVideo;
    }

    downloadVideo() {
        this.cameraService.downloadVideo(this.video);
    }

    addVideoObservation(videoObservation: VideoObservation): void {
        (document.activeElement as HTMLElement)?.blur();
        this.videoObservationSearchString = this.translationService.t('CompositeVideoPlayer.Added');

        if (this.video.MetaData.VideoObservations.indexOf(videoObservation) > -1) {
            return;
        }

        this.video.MetaData.VideoObservations.push(videoObservation);

        this.updateModel(this.videoEntryService.updateVideoEntry(this.video)).then(() => {
            this.createTags();

            if (this.onStatusUpdate) {
                this.onStatusUpdate();
            }
        });
    }

    removeVideoObservationUsingTag(tag: IVeriTag<VideoObservation>): void {
        if (!tag) {
            return;
        }

        this.video.MetaData.VideoObservations = this.video.MetaData.VideoObservations.filter(observation => observation !== tag?.data);

        this.updateModel(this.videoEntryService.updateVideoEntry(this.video)).then(() => {
            this.createTags();

            if (this.onStatusUpdate) {
                this.onStatusUpdate();
            }
        });
    }

    searchTag(searchString: string): VideoObservation[] {
        const assignedVideoObservations: VideoObservation[] = this.video?.MetaData?.VideoObservations;
        const uniqueVideoObservations: VideoObservation[] = Object.keys(VideoObservation)
            ?.filter(videoObservation => assignedVideoObservations?.indexOf(VideoObservation[videoObservation]) === -1 && typeof VideoObservation[videoObservation as any] === "number")
            ?.map(key => VideoObservation[key]);

        searchString = searchString?.toLowerCase()?.trim();

        if (!searchString) {
            return uniqueVideoObservations;
        }

        const filteredVideoObservations: VideoObservation[] = uniqueVideoObservations?.filter(videoObservation => {
            return VideoObservation[videoObservation]?.toLowerCase()?.trim()?.includes(searchString);
        });

        return filteredVideoObservations;
    }

    deleteVideo(): void {
        this.video.Status = VideoStatus.Archived;

        this.videoEntryService.updateVideoEntry(this.video).then(() => {
            if (this.onDelete) {
                this.onDelete();
            }
        });
    }

    onTimeUpdate($event: number) {
        if (!this.corneringChart || !this.brakingAccelerationChart) {
            return;
        }

        const secondsPlayed = $event;

        if (this.extensionData.VideoAccelerometerData && this.extensionData.VideoAccelerometerData.length > 0) {
            const progress = moment(this.extensionData.VideoAccelerometerData[0].Timestamp).add(secondsPlayed, "s");

            if (this.corneringChart?.xAxis) {
                this.chartService.addOrUpdateChartCrossHair(this.corneringChart?.xAxis[0], progress.valueOf());
            }

            if (this.brakingAccelerationChart.xAxis) {
                this.chartService.addOrUpdateChartCrossHair(this.brakingAccelerationChart?.xAxis[0], progress.valueOf());
            }
        }
    }

    // ---

    private updateVideoTimeline(): void {
        this.video.MetaData.timeline = this.videoReviewService.createVideoTimeline(this.video, this.users);
    }

    private createTags(): void {
        const tags: IVeriTag<VideoObservation>[] = [];

        this.video?.MetaData?.VideoObservations?.forEach(videoObservation => {
            if (!VideoObservation[videoObservation]) {
                return;
            }

            tags.push({
                id: tags.length,
                label: this.observationsService.setObservationTitleForDisplay(videoObservation),
                data: videoObservation,
                showRemove: true
            });
        });

        this.tags = [...tags];
    }

    private setVideoReviewStatus(): void {
        this.originalVideoReviewStatus = this.video.reviewStatus;
        this.updateMandatoryCommentRequired(false);
    }

    private updateMandatoryCommentRequired(required: boolean): void {
        this.mandatoryCommentRequired = required;
    }

    private addMissingReviewStatus(video: IVideoEntry): void {
        if (!video) {
            return;
        }

        this.videoReviewStatus = (!VideoReviewStatus[video.reviewStatus]) ? VideoReviewStatus.NeedsReview : video.reviewStatus;
        this.videoReviewSubStatus = video.reviewSubStatus;
        this.videoCoachingStatus = video.coachingStatus;
        this.originalVideoReviewStatus = this.videoReviewStatus;
    }

    private createBrakingAccelerationChartOptions() {
        const chartConfig = this.createChartOptions("CompositeVideoPlayer.BrakingAcceleration");

        this.createBasicAnnotations(chartConfig);

        var data = this.createBrakingAccelerationChartSeries();
        if (data?.length == 0) {
            return;
        }

        chartConfig.series = [
            {
                name: "x",
                data: data,
                type: "areaspline"
            }
        ];

        this.brakingAccelerationChartConfig = chartConfig;

        this.chartService.setOnLoadCallback(this.brakingAccelerationChartConfig, (chart) => {
            this.brakingAccelerationChart = chart;

            this.createPostLoadChartAnnotations(chart, this.brakingAccelerationChartConfig, this.translationService.t("CompositeVideoPlayer.Acceleration"), this.translationService.t("CompositeVideoPlayer.Braking"));
        });
    }

    private createCorneringChartOptions() {
        const chartConfig = this.createChartOptions(this.translationService.t("CompositeVideoPlayer.Cornering"));

        this.createBasicAnnotations(chartConfig);

        var data = this.createCorneringChartSeries();
        if (data?.length == 0) {
            return;
        }

        chartConfig.series = [
            {
                name: "y",
                data: this.createCorneringChartSeries(),
                type: "areaspline",
            }
        ];

        this.corneringChartConfig = chartConfig;

        this.chartService.setOnLoadCallback(this.corneringChartConfig, (chart) => {
            this.corneringChart = chart;

            this.createPostLoadChartAnnotations(chart, this.corneringChartConfig, this.translationService.t("CompositeVideoPlayer.Left"), this.translationService.t("CompositeVideoPlayer.Right"));
        });
    }

    private createBasicAnnotations(chartConfig: Highcharts.Options) {

        chartConfig.annotations = [{
            labelOptions: {
                backgroundColor: "transparent",
                borderColor: "transparent",
                style: {
                    color: "#999"
                }
            },
            labels: []
        }];
    }

    private createPostLoadChartAnnotations(chart: Highcharts.ChartObject, chartConfig: Highcharts.Options, topText: string, bottomText: string) {
        chartConfig.annotations = [{
            labels: [{
                point: { x: chart.plotSizeX, y: 0 },
                verticalAlign: "middle",
                text: topText,
                y: -2
            },
            {
                point: { x: chart.plotSizeY, y: chart.plotSizeY },
                verticalAlign: "middle",
                text: bottomText,
                y: 1
            }]
        }];
    }

    private createBrakingAccelerationChartSeries(): [number, number][] {
        var chartSeries: [number, number][] = [];

        this.extensionData?.VideoAccelerometerData?.forEach(accelerometerData => {
            // RTL Specific
            chartSeries.push([moment(accelerometerData.Timestamp).valueOf(), accelerometerData.X]);
        });

        return chartSeries;
    }

    private createChartOptions(title: string): Highcharts.Options {
        const chartConfig = this.chartService.createChartOptions("line");

        chartConfig.xAxis = {
            type: "datetime"
        };

        const redColour = "rgba(200, 50, 50)";
        const amberColour = "rgb(236, 170, 56)";
        const grayColour = "rgba(148, 148, 148, .85)";

        var amberBand = 'rgba(236, 170, 56, .05)';
        var redBand = "rgba(200, 50, 50, .10)";

        var amberBandFrom = .25;
        var amberBandTo = .5;

        var redBandFrom = .5;
        var redBandTo = 1;

        chartConfig.chart.marginTop = 10;
        chartConfig.chart.marginRight = 10;
        chartConfig.chart.plotBackgroundColor = "transparent";

        chartConfig.plotOptions.areaspline.zoneAxis = "y";
        chartConfig.plotOptions.areaspline.zones = [
            {
                value: -redBandFrom,
                color: redColour,
                fillColor: redBand,
            },
            {
                value: -amberBandFrom,
                color: amberColour,
                fillColor: amberBand
            },
            {
                value: amberBandFrom,
                color: grayColour,
            },
            {
                value: redBandFrom,
                color: amberColour,
                fillColor: amberBand
            },
            {
                color: redColour,
                fillColor: redBand
            }];

        chartConfig.yAxis = {
            title: {
                text: null
            },
            tickInterval: 0.40,
            minRange: 0.8 * 2
        }

        return chartConfig;
    }

    private createCorneringChartSeries(): [number, number][] {
        var chartSeries: [number, number][] = [];

        this.extensionData?.VideoAccelerometerData?.forEach(accelerometerData => {
            // This is RTL specific atm
            chartSeries.push([moment(accelerometerData.Timestamp).valueOf(), accelerometerData.Y]);
        });

        return chartSeries;
    }

    private async createMapEventMarker() {
        if (!this.video) {
            return;
        }

        this.videoMarker = null;
        this.videoMarker = await this.createVideoMapMarker();
        if (this.extensionData && this.extensionData.VideoGpsData.length) {
            const plots = this.extensionData.VideoGpsData.map(data => {
                return { Latitude: data.Latitude, Longitude: data.Longitude }
            });

            this.cameraDisplayService.createPolylinesFromPlots(plots, false).then(polyline => {
                this.createStartMarker(plots[0]);
                this.createEndMarker(plots[plots.length - 1]);

                this.videoPolyline = polyline;
            });
        } else {
            this.videoJourneys = [];
            this.journeyService.getJourneysForDateAndVehicle(this.video.VehicleId, this.video.VideoStartTime, null, true).then(
                journeys => {

                    this.videoJourney =
                        journeys.find(
                            j => moment(this.video.VideoStartTime).isBetween(j.StartUtc, j.EndUtc, null, "[]"));
                    if (this.videoJourney) {
                        this.videoJourneys = /*journeys*/[this.videoJourney];
                    }

                });
        }
    }

    private createStartMarker(plot: IPlot): void {
        this.startMapMarker = this.createMapMarker(plot, { label: this.translationService.t("CompositeVideoPlayer.Start"), subIcon: "play_arrow", showLabel: true });
    }

    private createEndMarker(plot: IPlot): void {
        this.endMapMarker = this.createMapMarker(plot, { label: this.translationService.t("CompositeVideoPlayer.End"), subIcon: "flag", showLabel: true });
    }

    private createMapMarker(plot: IPlot, options: ICreateEventMarkerOptions): IGoogleMapsMarker {
        options.position = {
            lat: plot.Latitude,
            lng: plot.Longitude
        };

        return {
            id: 1,
            options: this.googleMarkerService.createEventMarkerOptions(options)
        };
    }

    private setShowDownloadProgress(show: boolean) {
        this.showDownloadProgress = show;

        if (!show) {
            return;
        }

        this.cameraStatusService.getVideoChannels(this.video.VehicleId).then(channels => {
            this.video.vehicle.videoChannels = channels;

            this.downloadProgress = VideoEntryUtils.getVideoDownloadProgress(this.video);
            this.downloadStatus = this.cameraStatusService.getVideoRequestStatus(this.video);
        });
    }

    private populateVideoVehicleAndDriver() {
        if (this.video.driver != null) {
            return;
        }

        this.driverAssignmentService.getDriverAssignmentForTime(this.video.VehicleId, moment(this.video.VideoStartTime)).then(driver => {
            this.video.driver = driver;
        });
    }

    private createVideoMapMarker(): Promise<IGoogleMapsMarker<IVideoEntry>> {
        if (!this.video) {
            return;
        }

        var min = 1;
        var max = 10000;
        var randId = Math.floor(Math.random() * (max - min + 1)) + min;

        return this.geocodeService.getLocationInformationFromVideo(this.video.Id).then(location => {

            return {
                id: randId,
                model: this.video,
                options: this.googleMarkerService.createEventMarkerOptions({
                    label: location.LocationDescription,
                    subIcon: "videocam",
                    showLabel: true,
                    position: {
                        lat: this.video.Latitude, lng: this.video.Longitude
                    }
                })
            }
        }) as Promise<IGoogleMapsMarker<IVideoEntry>>;
    }

    private isUserActionSet(userActions: IUserAction[], user: IUser): boolean {
        var isSet = false;

        userActions.forEach(action => {
            if (action.UserId === user.Id) {
                isSet = true;
            }
        });

        return isSet;
    }
}

export class CompositeVideoPlayerComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            video: "<",
            showCoachingOnStartup: "<showCoaching",

            onDelete: "&",
            onStatusUpdate: "&",
        };

        this.controller = ComponentController;
        this.template = require("./compositeVideoPlayer.html");
    }
}
